import { Box, Feedback } from '@cubeyou-team/hx-react-components';
import React, { FC, useCallback, useState } from 'react';
import styled from 'styled-components';

import { useAudienceActivationContext } from 'src/modules/audience-activation/providers/AudienceActivationProvider';

import translations from 'src/lang';
import { SelectedDestinationItem } from 'src/modules/audience-activation/types';
import { RemoveDestinationModal } from '../RemoveDestinationModal';
import { SelectedDestinationCard } from './SelectedDestinationCard';

const lang = 'en-US';

const StyledDestinationsList = styled.div`
  display: grid;
  grid-template-columns: repeat(4, auto);
  justify-content: flex-start;
  gap: 0.625rem;
`;

interface SelectedDestinationsListProps {
  destinationDetails: SelectedDestinationItem;
  onEdit: (destination: string) => void;
  setDestinationDetails: (details: any) => void;
}

export const SelectedDestinationsList: FC<SelectedDestinationsListProps> = ({
  destinationDetails,
  onEdit,
  setDestinationDetails,
}) => {
  const {
    selectedDestinations,
    setSelectedDestinations,
  } = useAudienceActivationContext();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);

  const onDelete = useCallback(
    (id: string) => {
      const item = selectedDestinations.get(id);

      setDestinationDetails({ ...item });
      setIsDeleteModalOpen(true);
    },
    [selectedDestinations, setIsDeleteModalOpen, setDestinationDetails]
  );

  const handleRemoveDestinationModalClose = useCallback(() => {
    setDestinationDetails(null);
    setIsDeleteModalOpen(false);
  }, [setDestinationDetails, setIsDeleteModalOpen]);

  const handleDeleteDestination = useCallback(() => {
    const newDestinations = new Map(selectedDestinations);
    newDestinations.delete(destinationDetails.id);
    setSelectedDestinations(newDestinations);

    setIsDeleteModalOpen(false);
  }, [
    selectedDestinations,
    destinationDetails,
    setSelectedDestinations,
    setIsDeleteModalOpen,
  ]);

  if (!selectedDestinations.size) {
    return (
      <Box mB="small">
        <Feedback
          message={
            translations[lang].MODALS.AUDIENCE_ACTIVATION
              .DESTINATION_ERROR_MESSAGE
          }
          status="warning"
        />
      </Box>
    );
  }

  return (
    <>
      <StyledDestinationsList>
        {[...selectedDestinations?.keys()].map(item => {
          const {
            destination,
            image,
            isSelected,
            id,
          } = selectedDestinations.get(item);

          return (
            <SelectedDestinationCard
              destination={destination}
              key={id}
              title={destination}
              image={image}
              isSelected={isSelected}
              onDelete={() => onDelete(id)}
              onEdit={() => onEdit(id)}
            />
          );
        })}
      </StyledDestinationsList>
      <RemoveDestinationModal
        isOpen={isDeleteModalOpen}
        onClose={handleRemoveDestinationModalClose}
        onSuccess={handleDeleteDestination}
      />
    </>
  );
};
